import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useAuthStore } from "../../../../console/store/useAuthStore";
import { enum_api_uri } from "../../utils/enum";
import axiosInstance from "../axiosInstance";

export interface OrderData {
    order_idx: number;
    delivery_address_detail: string;
    order_number: string;
    delivery_date: string;
    delivery_time?: string | null; // 원문 (예식/행사/도착/까지 표기용)
    order_status: number;
    order_status_text: string;
    is_printed: boolean;
    branch_printed?: boolean; // 지점 포털 인수증 출력 여부 (본점 is_printed 와 별개)
    customer_manager_name: string;
    excel_date: string;
    customer_idx: number;
    customer_name: string;
    m_id: string | null;
    order_name: string;
    order_mobile: string;
    product_category: string;
    product_name: string;
    sales_amount: number;
    order_amount: number;
    recipient_name: string;
    wreath_sender: string;
    branch_type: number;
    branch_name: string;
    branch_del?: "Y" | "N"; // 지점 삭제 여부 (지점 포털에서 숨김, 본점은 배지 표시)
    payment_status: number;
    payment_status_text: string;
    order_date: string;
    delivery_address: string;
    event_content: {
        event_content_idx: number;
        depth1_idx: number;
        depth1: string;
        depth2: string | null;
        event_content: string;
    };
    product_quantity: number;
    customer_request: string;
    branch_req?: string | null; // 타지점 요구사항 (지점 포털 특이사항 컬럼)
    wreath_word?: string; // 경조사어
    ready_images?: string[]; // 지점이 올린 준비완료 사진
    photo_checked?: boolean; // 본점이 준비완료 사진을 확인했는지 (확인하면 점멸이 멈춘다)
    ready_by_admin?: boolean; // 고려원예 관리자가 준비완료로 바꿨는지 (관리자 변경 건은 점멸 대상이 아니다)
}

interface GetMemberResponse {
    limit: number;
    current_page: number;
    start_page: number;
    max_page: number;
    last_page: number;
    end_page: number;
    total_count: number;
    data_list: OrderData[];
}

export const useGetOrder = (
    page: number,
    pageSize: number,
    search?: string,
    searchtxt?: string,
    filterData?: {
        start_date: string;
        end_date: string;
        today_order?: string;
        order_status: number[];
        payment_status: number[];
        branch_type: number[];
        selected_customers?: string[];
        selected_branches?: string[];
        selected_delivery?: string[];
        userOrder?: string;
        photo_unchecked?: string; // 준비완료 사진 미확인만 보기
    },
    sortField?: string,
    sortOrder?: string,
    userOrder?: string,
): UseQueryResult<GetMemberResponse, Error> => {
    const accessToken = useAuthStore(state => state.accessToken);

    return useQuery<GetMemberResponse, Error>({
        queryKey: ["getOrderList", page, pageSize, search, searchtxt, filterData, sortOrder, sortField, userOrder],
        queryFn: async () => {
            const response = await axiosInstance.get(`${enum_api_uri.get_order}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                params: {
                    page,
                    limit: pageSize,
                    search,
                    searchtxt,
                    start_date: filterData?.start_date || undefined,
                    end_date: filterData?.end_date || undefined,
                    order_status: filterData?.order_status,
                    payment_status: filterData?.payment_status || undefined,
                    branch_type: filterData?.branch_type || undefined,
                    customer_idx: filterData?.selected_customers || undefined,
                    customer_branch_idx: filterData?.selected_branches || undefined,
                    delivery_name: filterData?.selected_delivery || undefined,
                    field: sortField,
                    direction: sortOrder,
                    today_order: filterData?.today_order || undefined,
                    photo_unchecked: filterData?.photo_unchecked || undefined,
                    userOrder: userOrder || undefined,
                },
            });
            return response.data.data;
        },
        placeholderData: prevData => {
            if (prevData) {
                return {
                    ...prevData,
                    current_page: page,
                };
            }
            return undefined;
        },
    });
};
